import React, { useMemo, useState, useEffect } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { EmployeeImage } from '@/components/EmployeeImage';
import { DataTableEnhanced } from '@/components/ui/data-table-enhanced';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle,
  AlertCircle,
  Users,
  Calendar,
  MessageSquare,
  Download,
  FileWarning
} from 'lucide-react';
import { PageHeader } from '@/components/blocks/page-header';
import { useDataSource } from '@/contexts/DataSourceContext';
import { missingTimesheetsApi, type MissingTimesheet as ApiMissingTimesheet, type MissingTimesheetSummary } from '@/services/missingTimesheetsApi';

// Missing Timesheet Data Type
interface MissingTimesheet {
  id: string;
  employeeName: string;
  employeeEmail: string;
  department: string;
  manager: string;
  weekEnding: string;
  status: 'Missing' | 'Draft' | 'Rejected';
  daysOverdue: number;
  severity: 'grace' | 'warning' | 'urgent' | 'critical';
  issues: {
    type: 'missing_days' | 'under_hours' | 'no_project' | 'unapproved' | 'rejected' | 'late_submission';
    description: string;
    severity: 'error' | 'warning' | 'info';
  }[];
  missingDays?: string[]; // e.g., ['Mon', 'Tue', 'Wed']
  expectedHours?: number;
  submittedHours?: number;
  lastActivity?: string; // Last time they accessed the timesheet
  lastReminded?: string; // Last time a reminder was sent
  photoUrl?: string; // Employee photo URL
}

// Mock data for missing timesheets
const mockMissingTimesheets: MissingTimesheet[] = [
  {
    id: '1',
    employeeName: 'John Smith',
    employeeEmail: 'john.smith@company.com',
    department: 'Engineering',
    manager: 'Sarah Wilson',
    weekEnding: '19/01/2026',
    status: 'Missing',
    daysOverdue: 12,
    severity: 'critical',
    issues: [
      { type: 'missing_days', description: 'No timesheet submitted', severity: 'error' },
      { type: 'late_submission', description: '12 days overdue', severity: 'error' },
    ],
    expectedHours: 40,
    submittedHours: 0,
    lastActivity: '07/01/2026',
    lastReminded: '15/01/2026',
  },
  {
    id: '2',
    employeeName: 'Alice Doe',
    employeeEmail: 'alice.doe@company.com',
    department: 'Sales',
    manager: 'Mike Brown',
    weekEnding: '19/01/2026',
    status: 'Draft',
    daysOverdue: 8,
    severity: 'urgent',
    issues: [
      { type: 'missing_days', description: 'Missing Mon, Tue, Wed', severity: 'error' },
      { type: 'under_hours', description: '16 of 40 hours submitted', severity: 'warning' },
    ],
    missingDays: ['Mon', 'Tue', 'Wed'],
    expectedHours: 40,
    submittedHours: 16,
    lastActivity: '11/01/2026',
    lastReminded: '17/01/2026',
  },
  {
    id: '3',
    employeeName: 'Kevin Lee',
    employeeEmail: 'kevin.lee@company.com',
    department: 'HR',
    manager: 'Jane Davis',
    weekEnding: '19/01/2026',
    status: 'Missing',
    daysOverdue: 4,
    severity: 'warning',
    issues: [
      { type: 'missing_days', description: 'No timesheet submitted', severity: 'error' },
    ],
    expectedHours: 40,
    submittedHours: 0,
    lastActivity: '15/01/2026',
    lastReminded: '18/01/2026',
  },
  {
    id: '4',
    employeeName: 'Sarah Johnson',
    employeeEmail: 'sarah.j@company.com',
    department: 'Finance',
    manager: 'Lisa White',
    weekEnding: '12/01/2026',
    status: 'Missing',
    daysOverdue: 1,
    severity: 'grace',
    issues: [
      { type: 'missing_days', description: 'No timesheet submitted', severity: 'warning' },
    ],
    expectedHours: 40,
    submittedHours: 0,
    lastActivity: '18/01/2026',
  },
  {
    id: '5',
    employeeName: 'Robert Taylor',
    employeeEmail: 'robert.t@company.com',
    department: 'Engineering',
    manager: 'Sarah Wilson',
    weekEnding: '19/01/2026',
    status: 'Missing',
    daysOverdue: 9,
    severity: 'urgent',
    issues: [
      { type: 'missing_days', description: 'No timesheet submitted', severity: 'error' },
      { type: 'late_submission', description: '9 days overdue', severity: 'error' },
    ],
    expectedHours: 40,
    submittedHours: 0,
    lastActivity: '10/01/2026',
  },
  {
    id: '6',
    employeeName: 'Emily Brown',
    employeeEmail: 'emily.b@company.com',
    department: 'Sales',
    manager: 'Mike Brown',
    weekEnding: '19/01/2026',
    status: 'Draft',
    daysOverdue: 5,
    severity: 'warning',
    issues: [
      { type: 'missing_days', description: 'Missing Thu, Fri', severity: 'error' },
      { type: 'under_hours', description: '24 of 40 hours submitted', severity: 'warning' },
    ],
    missingDays: ['Thu', 'Fri'],
    expectedHours: 40,
    submittedHours: 24,
    lastActivity: '14/01/2026',
  },
  {
    id: '7',
    employeeName: 'Daniel Anderson',
    employeeEmail: 'daniel.a@company.com',
    department: 'Engineering',
    manager: 'Sarah Wilson',
    weekEnding: '19/01/2026',
    status: 'Draft',
    daysOverdue: 6,
    severity: 'urgent',
    issues: [
      { type: 'missing_days', description: 'Missing Mon, Tue', severity: 'error' },
      { type: 'under_hours', description: '24 of 40 hours submitted', severity: 'warning' },
    ],
    missingDays: ['Mon', 'Tue'],
    expectedHours: 40,
    submittedHours: 24,
    lastActivity: '13/01/2026',
  },
  {
    id: '8',
    employeeName: 'Lisa Thompson',
    employeeEmail: 'lisa.t@company.com',
    department: 'Marketing',
    manager: 'Tom Anderson',
    weekEnding: '12/01/2026',
    status: 'Missing',
    daysOverdue: 2,
    severity: 'grace',
    issues: [
      { type: 'missing_days', description: 'No timesheet submitted', severity: 'warning' },
    ],
    expectedHours: 40,
    submittedHours: 0,
    lastActivity: '17/01/2026',
  },
  {
    id: '9',
    employeeName: 'Maria Garcia',
    employeeEmail: 'maria.g@company.com',
    department: 'Engineering',
    manager: 'Sarah Wilson',
    weekEnding: '19/01/2026',
    status: 'Missing',
    daysOverdue: 11,
    severity: 'critical',
    issues: [
      { type: 'missing_days', description: 'No timesheet submitted', severity: 'error' },
      { type: 'late_submission', description: '11 days overdue', severity: 'error' },
      { type: 'rejected', description: 'Previous timesheet rejected', severity: 'warning' },
    ],
    expectedHours: 40,
    submittedHours: 0,
    lastActivity: '08/01/2026',
  },
  {
    id: '10',
    employeeName: 'James Wilson',
    employeeEmail: 'james.w@company.com',
    department: 'Operations',
    manager: 'Chris Martin',
    weekEnding: '19/01/2026',
    status: 'Draft',
    daysOverdue: 3,
    severity: 'warning',
    issues: [
      { type: 'missing_days', description: 'Missing Wed', severity: 'error' },
      { type: 'under_hours', description: '32 of 40 hours submitted', severity: 'warning' },
    ],
    missingDays: ['Wed'],
    expectedHours: 40,
    submittedHours: 32,
    lastActivity: '16/01/2026',
  },
  {
    id: '11',
    employeeName: 'Patricia Martinez',
    employeeEmail: 'patricia.m@company.com',
    department: 'Finance',
    manager: 'Lisa White',
    weekEnding: '19/01/2026',
    status: 'Missing',
    daysOverdue: 13,
    severity: 'critical',
    issues: [
      { type: 'missing_days', description: 'No timesheet submitted', severity: 'error' },
      { type: 'late_submission', description: '13 days overdue', severity: 'error' },
    ],
    expectedHours: 40,
    submittedHours: 0,
    lastActivity: '06/01/2026',
  },
  {
    id: '12',
    employeeName: 'Michael Davis',
    employeeEmail: 'michael.d@company.com',
    department: 'HR',
    manager: 'Jane Davis',
    weekEnding: '12/01/2026',
    status: 'Missing',
    daysOverdue: 7,
    severity: 'urgent',
    issues: [
      { type: 'missing_days', description: 'No timesheet submitted', severity: 'error' },
      { type: 'late_submission', description: '7 days overdue', severity: 'error' },
    ],
    expectedHours: 40,
    submittedHours: 0,
    lastActivity: '12/01/2026',
  },
];

// Helper function to generate a random photo URL for employees without avatars
const getDemoPhotoUrl = (name: string, id: string): string => {
  const seed = encodeURIComponent(name.replace(/\s+/g, '-'));
  return `https://picsum.photos/seed/${seed}-${id}/300/300`;
};

// Helper function to check if URL is an Evo avatar (not from dicebear or other non-Evo sources)
const isEvoAvatarUrl = (url: string): boolean => {
  const trimmedUrl = url.trim().toLowerCase();
  if (trimmedUrl.includes('dicebear')) {
    return false;
  }
  return true;
};

// Helper function to get photo URL - uses Evo avatar if available, otherwise random image
const getPhotoUrl = (name: string, id: string, photoUrl?: string | null): string => {
  if (photoUrl && photoUrl.trim().length > 0 && isEvoAvatarUrl(photoUrl)) {
    return photoUrl;
  }
  return getDemoPhotoUrl(name, id);
};

// Helper function to get severity color
const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'critical':
      return 'bg-error-500';
    case 'urgent':
      return 'bg-warning-500';
    case 'warning':
      return 'bg-warning-300';
    case 'grace':
      return 'bg-success-500';
    default:
      return 'bg-muted';
  }
};

// Helper function to get severity label
const getSeverityLabel = (severity: string) => {
  switch (severity) {
    case 'critical':
      return '🔴 Critical (11+ days)';
    case 'urgent':
      return '🟠 Urgent (6-10 days)';
    case 'warning':
      return '🟡 Warning (3-5 days)';
    case 'grace':
      return '🟢 Grace (0-2 days)';
    default:
      return severity;
  }
};

export const MissingTimesheetsReportPage: React.FC = () => {
  const { getScreenMode } = useDataSource();
  const screenMode = getScreenMode('reports-missing-timesheets');
  
  // State for API data
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiMissingTimesheets, setApiMissingTimesheets] = useState<MissingTimesheet[]>([]);
  const [apiSummary, setApiSummary] = useState<MissingTimesheetSummary | null>(null);
  
  // State for selected rows
  const [selectedEmployees, setSelectedEmployees] = useState<MissingTimesheet[]>([]);
  const [clearSelection, setClearSelection] = useState(false);

  // Fetch data from API when in live mode
  useEffect(() => {
    if (screenMode === 'live') {
      const fetchMissingTimesheets = async () => {
        setIsLoading(true);
        setError(null);
        
        try {
          const [timesheetsResponse, summaryResponse] = await Promise.all([
            missingTimesheetsApi.getMissingTimesheets({ pageSize: 100 }),
            missingTimesheetsApi.getSummary()
          ]);
          
          // Transform API data to match local interface
          const transformedData: MissingTimesheet[] = timesheetsResponse.data.map((item: ApiMissingTimesheet) => ({
            id: item.id,
            employeeName: item.employeeName,
            employeeEmail: item.employeeEmail,
            department: item.department,
            manager: item.managerName || 'Unknown',
            weekEnding: item.weekEnding,
            status: item.status,
            daysOverdue: item.daysOverdue,
            severity: item.severity,
            issues: [], // API doesn't return issues, we'll derive them
            missingDays: item.missingDays,
            expectedHours: item.expectedHours,
            submittedHours: item.submittedHours,
            lastActivity: item.lastActivity,
            lastReminded: item.lastReminded,
            photoUrl: getPhotoUrl(item.employeeName, item.id, item.photoUrl),
          }));
          
          setApiMissingTimesheets(transformedData);
          setApiSummary(summaryResponse);
        } catch (err) {
          console.error('Error fetching missing timesheets:', err);
          setError(err instanceof Error ? err.message : 'Failed to load missing timesheets');
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchMissingTimesheets();
    }
  }, [screenMode]);

  // Use API data when in live mode, mock data when in demo mode
  // For demo mode, add random photo URLs to mock data
  const missingTimesheets = useMemo(() => {
    if (screenMode === 'live') {
      return apiMissingTimesheets;
    }
    // Add photo URLs to mock data for demo mode
    return mockMissingTimesheets.map(item => ({
      ...item,
      photoUrl: getPhotoUrl(item.employeeName, item.id, item.photoUrl),
    }));
  }, [screenMode, apiMissingTimesheets]);

  // Handler functions
  const handleSendReminders = async () => {
    if (selectedEmployees.length === 0) {
      const totalEmployees = missingTimesheets.length;
      console.log('Sending reminders to ALL employees with missing timesheets');
      
      if (screenMode === 'live' && missingTimesheets.length > 0) {
        try {
          // Get unique week endings from the data
          const weekEnding = missingTimesheets[0]?.weekEnding || '';
          const employeeIds = missingTimesheets.map(t => parseInt(t.id));
          await missingTimesheetsApi.sendReminders(employeeIds, weekEnding);
          toast.success(`Successfully sent feed notifications to all ${totalEmployees} employee${totalEmployees > 1 ? 's' : ''} with missing timesheets.`, {
            description: 'Reminders have been sent via Evo Feeds',
          });
        } catch (err) {
          console.error('Error sending reminders:', err);
          toast.error('Failed to send reminders', {
            description: err instanceof Error ? err.message : 'An error occurred',
          });
        }
      } else {
        toast.success(`Successfully sent feed notifications to all ${totalEmployees} employee${totalEmployees > 1 ? 's' : ''} with missing timesheets.`, {
          description: 'Reminders have been sent via Evo Feeds',
        });
      }
    } else {
      console.log('Sending reminders to selected employees:', selectedEmployees.map(e => e.employeeName));
      
      if (screenMode === 'live') {
        try {
          const weekEnding = selectedEmployees[0]?.weekEnding || '';
          const employeeIds = selectedEmployees.map(e => parseInt(e.id));
          await missingTimesheetsApi.sendReminders(employeeIds, weekEnding);
          toast.success(`Successfully sent feed notifications to ${selectedEmployees.length} employee${selectedEmployees.length > 1 ? 's' : ''}.`, {
            description: 'Reminders have been sent via Evo Feeds',
          });
        } catch (err) {
          console.error('Error sending reminders:', err);
          toast.error('Failed to send reminders', {
            description: err instanceof Error ? err.message : 'An error occurred',
          });
        }
      } else {
        toast.success(`Successfully sent feed notifications to ${selectedEmployees.length} employee${selectedEmployees.length > 1 ? 's' : ''}.`, {
          description: 'Reminders have been sent via Evo Feeds',
        });
      }
      
      // Clear selection after sending
      setSelectedEmployees([]);
      setClearSelection(true);
      // Reset clearSelection flag after a brief delay
      setTimeout(() => setClearSelection(false), 100);
    }
  };

  const handleExport = () => {
    console.log('Export button clicked');
    console.log('Missing timesheets:', missingTimesheets);
    
    // Check if there's data to export
    if (!missingTimesheets || missingTimesheets.length === 0) {
      toast.error('No data to export', {
        description: 'There are no missing timesheets to export.',
      });
      return;
    }

    // Prepare data for export
    const exportData = missingTimesheets.map(timesheet => ({
      'Employee': timesheet.employeeName,
      'Status': timesheet.status === 'Missing' ? 'Missing' : timesheet.status === 'Draft' ? 'Draft' : 'Rejected',
      'Week Ending': new Date(timesheet.weekEnding).toLocaleDateString('en-GB'),
      'Expected Hours': timesheet.expectedHours,
      'Submitted Hours': timesheet.submittedHours,
      'Missing Days': timesheet.missingDays ? timesheet.missingDays.join(', ') : '',
      'Days Overdue': timesheet.daysOverdue,
      'Last Reminded': timesheet.lastReminded || 'Never',
      'Department': timesheet.department,
      'Manager': timesheet.manager,
    }));
    
    console.log('Export data prepared:', exportData);

    // Convert to CSV
    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map(row => 
        headers.map(header => {
          const value = row[header as keyof typeof row];
          // Escape values that contain commas or quotes
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().split('T')[0];
    
    link.setAttribute('href', url);
    link.setAttribute('download', `missing-timesheets-${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Show success toast
    toast.success('Report exported successfully', {
      description: `Downloaded missing-timesheets-${timestamp}.csv`,
    });
  };

  // Calculate summary metrics
  const metrics = useMemo(() => {
    // Use API summary when in live mode and available
    if (screenMode === 'live' && apiSummary) {
      return {
        totalMissing: apiSummary.totalMissing,
        uniqueEmployees: apiSummary.uniqueEmployees,
        complianceRate: apiSummary.complianceRate.toFixed(1),
        avgDaysOverdue: apiSummary.avgDaysOverdue.toFixed(1),
        repeatOffenders: apiSummary.repeatOffenders,
        // Comparison values from API
        totalMissingChange: apiSummary.totalMissingChange ?? 0,
        complianceRateChange: apiSummary.complianceRateChange ?? 0,
        avgDaysOverdueChange: apiSummary.avgDaysOverdueChange ?? 0,
        repeatOffendersChange: apiSummary.repeatOffendersChange ?? 0,
      };
    }
    
    // Fall back to calculating from local data
    const total = missingTimesheets.length;
    const uniqueEmployees = new Set(missingTimesheets.map(t => t.employeeName)).size;
    const totalTimesheetsExpected = 500; // Mock total timesheets due
    const complianceRate = ((totalTimesheetsExpected - total) / totalTimesheetsExpected) * 100;
    const avgDaysOverdue = total > 0 ? missingTimesheets.reduce((sum, t) => sum + t.daysOverdue, 0) / total : 0;
    // Repeat offenders: employees with critical severity (11+ days overdue)
    const repeatOffenders = missingTimesheets.filter(t => t.severity === 'critical').length;

    return {
      totalMissing: total,
      uniqueEmployees,
      complianceRate: complianceRate.toFixed(1),
      avgDaysOverdue: avgDaysOverdue.toFixed(1),
      repeatOffenders,
      // Mock comparison values for demo mode
      totalMissingChange: -12,
      complianceRateChange: 2.1,
      avgDaysOverdueChange: -0.8,
      repeatOffendersChange: -3,
    };
  }, [screenMode, apiSummary, missingTimesheets]);

  // Define columns for DataTableEnhanced
  const columns: ColumnDef<MissingTimesheet>[] = useMemo(() => [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'employeeName',
      header: 'Employee',
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <EmployeeImage
            name={row.original.employeeName}
            photoUrl={row.original.photoUrl}
            size="sm"
          />
          <div className="font-medium">{row.original.employeeName}</div>
        </div>
      ),
      enableSorting: true,
      enableGrouping: true,
    },
    {
      accessorKey: 'department',
      header: 'Department',
      enableSorting: true,
      enableGrouping: true,
    },
    {
      accessorKey: 'manager',
      header: 'Manager',
      enableSorting: true,
      enableGrouping: true,
    },
    {
      accessorKey: 'weekEnding',
      header: 'Week Ending',
      enableSorting: true,
      enableGrouping: true,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.status === 'Missing' ? 'destructive' : 'secondary'}>
          {row.original.status}
        </Badge>
      ),
      enableSorting: true,
      enableGrouping: true,
    },
    {
      accessorKey: 'expectedHours',
      header: 'Expected Hours',
      cell: ({ row }) => {
        const submittedHours = row.original.submittedHours || 0;
        const expectedHours = row.original.expectedHours || 40;
        const missingDays = row.original.missingDays || [];

        return (
          <div className="space-y-1">
            <div className="text-sm font-medium">
              {submittedHours} / {expectedHours} hours
            </div>
            {missingDays.length > 0 && (
              <div className="text-xs text-muted-foreground">
                Missing: {missingDays.join(', ')}
              </div>
            )}
          </div>
        );
      },
      enableSorting: true,
      enableGrouping: false,
    },
    {
      accessorKey: 'daysOverdue',
      header: 'Days Overdue',
      cell: ({ row }) => (
        <span className="font-medium">{row.original.daysOverdue}</span>
      ),
      enableSorting: true,
      aggregationFn: 'mean',
      aggregatedCell: ({ getValue }) => `Avg: ${(getValue() as number).toFixed(1)}`,
    },
    {
      accessorKey: 'lastReminded',
      header: 'Last Reminded',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.lastReminded || 'Never'}
        </span>
      ),
      enableSorting: true,
    },

    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => console.log('Send feed notification', row.original.employeeName)}
            title="Send Evo Feed notification"
          >
            <MessageSquare className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ], []);

  // Field labels for better UX
  const fieldLabels: Record<string, string> = {
    severity: 'Severity',
    employeeName: 'Employee',
    department: 'Department',
    manager: 'Manager',
    weekEnding: 'Week Ending',
    status: 'Status',
    expectedHours: 'Expected Hours',
    daysOverdue: 'Days Overdue',
    lastReminded: 'Last Reminded',
  };

  // Field categories for organization
  const fieldCategories: Record<string, string> = {
    severity: 'Priority',
    employeeName: 'People',
    department: 'Organization',
    manager: 'Organization',
    weekEnding: 'Time Period',
    status: 'Status',
    expectedHours: 'Metrics',
    daysOverdue: 'Metrics',
    lastReminded: 'Activity',
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-10 w-72 mb-3" />
          <Skeleton className="h-5 w-96" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-36" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Missing Timesheets Report"
          subtitle="Track and manage missing timesheet submissions across your organization"
          size={36}
          showActions={false}
          showBreadcrumb={false}
          icon={<FileWarning />}
        />
        <Card className="border-error-200 bg-error-50 dark:bg-error-950/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-error-600" />
              <div>
                <p className="font-medium text-error-900 dark:text-error-100">Failed to load missing timesheets</p>
                <p className="text-sm text-error-700 dark:text-error-300">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Missing Timesheets Report"
        subtitle="Track and manage missing timesheet submissions across your organization"
        size={36}
        showActions={false}
        showBreadcrumb={false}
        icon={<FileWarning />}
      />

      {/* Summary Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Missing</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalMissing}</div>
            <p className="text-xs text-muted-foreground">
              {metrics.uniqueEmployees} employees
            </p>
            <div className={`flex items-center text-xs mt-1 ${metrics.totalMissingChange <= 0 ? 'text-success-600' : 'text-error-600'}`}>
              {metrics.totalMissingChange <= 0 ? <TrendingDown className="h-3 w-3 mr-1" /> : <TrendingUp className="h-3 w-3 mr-1" />}
              {metrics.totalMissingChange <= 0 ? '' : '+'}{metrics.totalMissingChange} vs last period
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Compliance Rate</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.complianceRate}%</div>
            <p className="text-xs text-muted-foreground">
              of timesheets submitted
            </p>
            <div className={`flex items-center text-xs mt-1 ${metrics.complianceRateChange >= 0 ? 'text-success-600' : 'text-error-600'}`}>
              {metrics.complianceRateChange >= 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
              {metrics.complianceRateChange >= 0 ? '+' : ''}{Number(metrics.complianceRateChange).toFixed(1)}% vs last period
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Days Overdue</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.avgDaysOverdue}</div>
            <p className="text-xs text-muted-foreground">
              days on average
            </p>
            <div className={`flex items-center text-xs mt-1 ${metrics.avgDaysOverdueChange <= 0 ? 'text-success-600' : 'text-error-600'}`}>
              {metrics.avgDaysOverdueChange <= 0 ? <TrendingDown className="h-3 w-3 mr-1" /> : <TrendingUp className="h-3 w-3 mr-1" />}
              {metrics.avgDaysOverdueChange <= 0 ? '' : '+'}{Number(metrics.avgDaysOverdueChange).toFixed(1)} days vs last period
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Repeat Offenders</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.repeatOffenders}</div>
            <p className="text-xs text-muted-foreground">
              11+ days overdue
            </p>
            <div className={`flex items-center text-xs mt-1 ${metrics.repeatOffendersChange <= 0 ? 'text-success-600' : 'text-error-600'}`}>
              {metrics.repeatOffendersChange <= 0 ? <TrendingDown className="h-3 w-3 mr-1" /> : <TrendingUp className="h-3 w-3 mr-1" />}
              {metrics.repeatOffendersChange <= 0 ? '' : '+'}{metrics.repeatOffendersChange} vs last period
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Missing Timesheets Table with EDS DataTableEnhanced */}
      <Card>
        <CardHeader>
          <CardTitle>Missing Timesheets</CardTitle>
          <CardDescription>
            All employees with missing or incomplete timesheet submissions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTableEnhanced
            columns={columns}
            data={missingTimesheets}
            searchKey="employeeName"
            searchPlaceholder="Search employees..."
            enableGrouping={true}
            enableColumnVisibility={true}
            enableSorting={true}
            enableFiltering={true}
            enableRowSelection={true}
            onSelectionChange={setSelectedEmployees}
            clearSelection={clearSelection}
            pageSize={20}
            storageKey="missing-timesheets-report"
            fieldLabels={fieldLabels}
            fieldCategories={fieldCategories}
            showSortIconsWhenNotSorted={false}
            toolbarActions={
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSendReminders}
                  className="gap-2"
                >
                  <MessageSquare className="h-4 w-4" />
                  {selectedEmployees.length > 0 
                    ? `Send Reminders (${selectedEmployees.length})`
                    : 'Send Reminders (All)'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Export
                </Button>
              </>
            }
          />
        </CardContent>
      </Card>
    </div>
  );
};
